import React from "react";
import {
    Database,
    Play,
    Plus,
    Trash2,
    CheckCircle2,
    XCircle,
    Loader2,
    FileCode2,
    History,
    ShieldCheck,
    RefreshCw,
    Search,
    Circle,
    Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
    thuriyamService,
    type SQLScript,
    type ScriptExecution,
    type ScriptPermission,
} from "./thuriyamApi";

type SubTab = "scripts" | "executions" | "permissions";

// ── helpers ───────────────────────────────────────────────────────────────────

const DB_LABELS: Record<string, string> = {
    s1_apply_db:   "S1 - Apply Database",
    s2_applyv2_db: "S2 - ApplyV2 Database",
    both:          "Both S1 and S2",
};

function dbLabel(s: SQLScript): string {
    return s.target_database_display ?? DB_LABELS[s.target_database] ?? s.target_database ?? "—";
}

function fmtDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit",
    });
}

function ActiveDot({ active }: { active: boolean }) {
    return active ? (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100" title="Active">
            <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500" />
        </span>
    ) : (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100" title="Inactive">
            <Circle className="h-2.5 w-2.5 fill-gray-300 text-gray-300" />
        </span>
    );
}

// ── main component ────────────────────────────────────────────────────────────

const SqlScriptExecutorPanel: React.FC = () => {
    const { toast } = useToast();
    const [subTab, setSubTab] = React.useState<SubTab>("scripts");

    // ── Scripts list ──────────────────────────────────────────────────────────
    const [scripts, setScripts] = React.useState<SQLScript[]>([]);
    const [scriptsLoading, setScriptsLoading] = React.useState(false);
    const [scriptsSearch, setScriptsSearch] = React.useState("");
    const [searchInput, setSearchInput] = React.useState("");
    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    const [bulkAction, setBulkAction] = React.useState("");

    const fetchScripts = React.useCallback(async (search?: string) => {
        setScriptsLoading(true);
        setSelected(new Set());
        try {
            const q = search !== undefined ? search : scriptsSearch;
            const res = await thuriyamService.listScripts({ search: q || undefined });
            setScripts(res.data);
        } catch {
            toast({ title: "Failed to load scripts", variant: "destructive" });
        } finally {
            setScriptsLoading(false);
        }
    }, [scriptsSearch, toast]);

    React.useEffect(() => {
        if (subTab === "scripts") fetchScripts();
    }, [subTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        setScriptsSearch(searchInput);
        fetchScripts(searchInput);
    };

    const allSelected = scripts.length > 0 && selected.size === scripts.length;
    const toggleAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(scripts.map((s) => s.id)));
    };
    const toggleOne = (id: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleBulkGo = async () => {
        if (!bulkAction || selected.size === 0) return;
        if (bulkAction === "delete") {
            if (!window.confirm(`Delete ${selected.size} script(s)? This cannot be undone.`)) return;
            let failed = 0;
            for (const id of selected) {
                try { await thuriyamService.deleteScript(id); }
                catch { failed++; }
            }
            toast({
                title: failed === 0 ? `${selected.size} script(s) deleted` : `${selected.size - failed} deleted, ${failed} failed`,
                variant: failed > 0 ? "destructive" : "default",
            });
            fetchScripts();
        }
        setBulkAction("");
    };

    // ── Create / Edit script dialog ───────────────────────────────────────────
    const [scriptDialogOpen, setScriptDialogOpen] = React.useState(false);
    const [editingScript, setEditingScript] = React.useState<SQLScript | null>(null);
    const [scriptForm, setScriptForm] = React.useState({
        name: "", description: "", sql_content: "",
        target_database: "s1_apply_db", is_active: true,
        timeout_seconds: 30, max_rows: 1000,
    });
    const [scriptSaving, setScriptSaving] = React.useState(false);

    const openCreateScript = () => {
        setEditingScript(null);
        setScriptForm({ name: "", description: "", sql_content: "", target_database: "s1_apply_db", is_active: true, timeout_seconds: 30, max_rows: 1000 });
        setScriptDialogOpen(true);
    };

    const openEditScript = (s: SQLScript) => {
        setEditingScript(s);
        setScriptForm({
            name: s.name,
            description: s.description,
            sql_content: s.sql_query ?? "",
            target_database: s.target_database ?? "s1_apply_db",
            is_active: s.is_active,
            timeout_seconds: s.timeout_seconds ?? 30,
            max_rows: s.max_rows ?? 1000,
        });
        setScriptDialogOpen(true);
    };

    const handleSaveScript = async () => {
        if (!scriptForm.name.trim() || !scriptForm.sql_content.trim()) {
            toast({ title: "Name and SQL content are required", variant: "destructive" });
            return;
        }
        setScriptSaving(true);
        try {
            const payload = {
                name: scriptForm.name,
                description: scriptForm.description,
                sql_query: scriptForm.sql_content,
                target_database: scriptForm.target_database as 's1_apply_db' | 's2_applyv2_db' | 'both',
                is_active: scriptForm.is_active,
                timeout_seconds: scriptForm.timeout_seconds,
                max_rows: scriptForm.max_rows,
            };
            if (editingScript) {
                await thuriyamService.updateScript(editingScript.id, payload);
                toast({ title: "Script updated" });
            } else {
                await thuriyamService.createScript(payload);
                toast({ title: "Script created" });
            }
            setScriptDialogOpen(false);
            fetchScripts();
        } catch (err: any) {
            toast({ title: "Save failed", description: err?.response?.data?.error ?? String(err), variant: "destructive" });
        } finally {
            setScriptSaving(false);
        }
    };

    const handleDeleteScript = async (id: number) => {
        if (!window.confirm("Delete this script? This cannot be undone.")) return;
        try {
            await thuriyamService.deleteScript(id);
            toast({ title: "Script deleted" });
            fetchScripts();
        } catch {
            toast({ title: "Delete failed", variant: "destructive" });
        }
    };

    // ── Execute script dialog ─────────────────────────────────────────────────
    const [execDialogOpen, setExecDialogOpen] = React.useState(false);
    const [execScript, setExecScript] = React.useState<SQLScript | null>(null);
    const [execLoading, setExecLoading] = React.useState(false);
    const [execResult, setExecResult] = React.useState<any>(null);

    const openExecDialog = (s: SQLScript) => {
        setExecScript(s);
        setExecResult(null);
        setExecDialogOpen(true);
    };

    const handleExecute = async () => {
        if (!execScript) return;
        setExecLoading(true);
        setExecResult(null);
        try {
            const res = await thuriyamService.executeScript(execScript.id);
            setExecResult(res.data);
            if (res.data.status === 'success') {
                toast({ title: "Script executed", description: `${res.data.row_count ?? 0} row(s) affected.` });
                fetchScripts();
            } else {
                toast({ title: "Execution failed", description: res.data.error_message ?? "Unknown error", variant: "destructive" });
            }
        } catch (err: any) {
            setExecResult({ status: 'error', error_message: err?.response?.data?.error ?? String(err) });
        } finally {
            setExecLoading(false);
        }
    };

    // ── Executions list ───────────────────────────────────────────────────────
    const [executions, setExecutions] = React.useState<ScriptExecution[]>([]);
    const [executionsLoading, setExecutionsLoading] = React.useState(false);
    const [execFilterScriptId, setExecFilterScriptId] = React.useState<number | null>(null);
    const [execFilterScriptName, setExecFilterScriptName] = React.useState<string | null>(null);

    const fetchExecutions = React.useCallback(async (scriptId?: number | null) => {
        setExecutionsLoading(true);
        try {
            const sid = scriptId !== undefined ? scriptId : execFilterScriptId;
            const res = await thuriyamService.listExecutions({ page_size: 50, script_id: sid ?? undefined });
            setExecutions(res.data.results);
        } catch {
            toast({ title: "Failed to load executions", variant: "destructive" });
        } finally {
            setExecutionsLoading(false);
        }
    }, [execFilterScriptId, toast]);

    React.useEffect(() => {
        if (subTab === "executions") fetchExecutions();
    }, [subTab, fetchExecutions]);

    const openExecutionsForScript = (s: SQLScript) => {
        setExecFilterScriptId(s.id);
        setExecFilterScriptName(s.name);
        setSubTab("executions");
        fetchExecutions(s.id);
    };

    // ── Permissions list ──────────────────────────────────────────────────────
    const [permissions, setPermissions] = React.useState<ScriptPermission[]>([]);
    const [permissionsLoading, setPermissionsLoading] = React.useState(false);
    const [permFilterScriptId, setPermFilterScriptId] = React.useState<number | null>(null);
    const [permFilterScriptName, setPermFilterScriptName] = React.useState<string | null>(null);

    const fetchPermissions = React.useCallback(async (scriptId?: number | null) => {
        setPermissionsLoading(true);
        try {
            const sid = scriptId !== undefined ? scriptId : permFilterScriptId;
            const res = await thuriyamService.listPermissions({ script_id: sid ?? undefined });
            setPermissions(res.data);
        } catch {
            toast({ title: "Failed to load permissions", variant: "destructive" });
        } finally {
            setPermissionsLoading(false);
        }
    }, [permFilterScriptId, toast]);

    React.useEffect(() => {
        if (subTab === "permissions") fetchPermissions();
    }, [subTab, fetchPermissions]);

    const openPermissionsForScript = (s: SQLScript) => {
        setPermFilterScriptId(s.id);
        setPermFilterScriptName(s.name);
        setSubTab("permissions");
        fetchPermissions(s.id);
    };

    const handleRevokePermission = async (id: number) => {
        if (!window.confirm("Revoke this permission?")) return;
        try {
            await thuriyamService.revokePermission(id);
            toast({ title: "Permission revoked" });
            fetchPermissions();
        } catch {
            toast({ title: "Revoke failed", variant: "destructive" });
        }
    };

    // ── Tab config ────────────────────────────────────────────────────────────
    const SUB_TABS: { id: SubTab; label: string; icon: React.ElementType }[] = [
        { id: "scripts",     label: "SQL Scripts",   icon: FileCode2   },
        { id: "executions",  label: "Executions",    icon: History     },
        { id: "permissions", label: "Permissions",   icon: ShieldCheck },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-0">

            {/* Sub-tab bar */}
            <div className="flex gap-0 border-b border-gray-200 mb-5">
                {SUB_TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setSubTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                            subTab === t.id
                                ? "border-intellend-green text-intellend-green bg-intellend-green/5"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                        <t.icon className="h-4 w-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ================================================================
                SQL SCRIPTS TAB
            ================================================================ */}
            {subTab === "scripts" && (
                <div className="space-y-4">

                    {/* Title + Add button */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">Select SQL Script to change</h2>
                        <Button
                            size="sm"
                            onClick={openCreateScript}
                            className="bg-intellend-green hover:bg-intellend-green/90 text-white gap-1.5"
                        >
                            <Plus className="h-4 w-4" />
                            Add SQL Script
                        </Button>
                    </div>

                    {/* Search bar */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            <Input
                                placeholder="Search…"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                className="pl-8 h-9"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={handleSearch} className="h-9">
                            Search
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSearchInput(""); setScriptsSearch(""); fetchScripts(""); }}
                            className="h-9 text-gray-400 hover:text-gray-600"
                            title="Clear & refresh"
                        >
                            <RefreshCw className={`h-4 w-4 ${scriptsLoading ? "animate-spin" : ""}`} />
                        </Button>
                    </div>

                    {/* Bulk action bar */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Action:</span>
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                            className="h-8 rounded border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-intellend-green"
                        >
                            <option value="">---------</option>
                            <option value="delete">Delete selected SQL scripts</option>
                        </select>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleBulkGo}
                            disabled={!bulkAction || selected.size === 0}
                            className="h-8 px-3"
                        >
                            Go
                        </Button>
                        <span className="text-sm text-gray-400 ml-1">
                            {selected.size} of {scripts.length} selected
                        </span>
                    </div>

                    {/* Table */}
                    {scriptsLoading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400">
                            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
                        </div>
                    ) : scripts.length === 0 ? (
                        <div className="text-center py-16 text-gray-400 border border-dashed border-gray-200 rounded-xl">
                            <Database className="h-10 w-10 mx-auto mb-2 opacity-30" />
                            <p className="font-medium">No SQL scripts found.</p>
                            <p className="text-sm mt-1">Click "Add SQL Script" to create one.</p>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                        <TableHead className="w-10 pl-4">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleAll}
                                                className="rounded border-gray-300 text-intellend-green focus:ring-intellend-green cursor-pointer"
                                            />
                                        </TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Name</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Target Database</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide text-center">Is Active</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Created by</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Created at</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Executions</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide text-right pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {scripts.map((s) => (
                                        <TableRow
                                            key={s.id}
                                            className={`transition-colors ${selected.has(s.id) ? "bg-blue-50 hover:bg-blue-100/60" : "hover:bg-gray-50/80"}`}
                                        >
                                            <TableCell className="pl-4 w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(s.id)}
                                                    onChange={() => toggleOne(s.id)}
                                                    className="rounded border-gray-300 text-intellend-green focus:ring-intellend-green cursor-pointer"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <button
                                                    onClick={() => openEditScript(s)}
                                                    className="font-medium text-[#417690] hover:text-[#205067] hover:underline text-left leading-snug"
                                                >
                                                    {s.name}
                                                </button>
                                                {s.description && (
                                                    <p className="text-xs text-gray-400 mt-0.5 max-w-[220px] truncate">{s.description}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                                                    <Database className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                                    {dbLabel(s)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <ActiveDot active={s.is_active} />
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">
                                                {s.created_by ? s.created_by.username : <span className="text-gray-400">—</span>}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-500 whitespace-nowrap">
                                                {fmtDate(s.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                {(s.execution_count ?? 0) > 0 ? (
                                                    <button
                                                        onClick={() => openExecutionsForScript(s)}
                                                        className="text-sm text-[#417690] hover:text-[#205067] hover:underline font-medium"
                                                    >
                                                        {s.execution_count} execution{s.execution_count !== 1 ? "s" : ""}
                                                    </button>
                                                ) : (
                                                    <span className="text-sm text-gray-400">0 executions</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right pr-4">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => openExecDialog(s)}
                                                        disabled={!s.is_active}
                                                        className="h-7 px-3 text-xs bg-intellend-green hover:bg-intellend-green/90 text-white gap-1 disabled:opacity-40"
                                                    >
                                                        <Play className="h-3 w-3" />
                                                        Execute
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openPermissionsForScript(s)}
                                                        className="h-7 px-3 text-xs border-gray-300 text-gray-700 hover:bg-gray-50 gap-1"
                                                    >
                                                        <ShieldCheck className="h-3 w-3" />
                                                        Permissions
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleDeleteScript(s.id)}
                                                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                                        title="Delete script"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                                <span className="text-sm text-gray-600 font-medium">
                                    {scripts.length} SQL Script{scripts.length !== 1 ? "s" : ""}
                                </span>
                                {selected.size > 0 && (
                                    <span className="text-xs text-gray-400">{selected.size} selected</span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ================================================================
                EXECUTIONS TAB
            ================================================================ */}
            {subTab === "executions" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-lg font-semibold text-gray-800">Script Executions</h2>
                            {execFilterScriptName && (
                                <Badge variant="outline" className="text-xs gap-1 font-normal">
                                    Filtered: <span className="font-medium">{execFilterScriptName}</span>
                                    <button
                                        onClick={() => { setExecFilterScriptId(null); setExecFilterScriptName(null); fetchExecutions(null); }}
                                        className="ml-1 text-gray-400 hover:text-gray-700 leading-none"
                                        title="Clear filter"
                                    >
                                        ×
                                    </button>
                                </Badge>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => fetchExecutions()} disabled={executionsLoading} className="gap-1.5">
                            <RefreshCw className={`h-4 w-4 ${executionsLoading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>

                    {executionsLoading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400">
                            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
                        </div>
                    ) : executions.length === 0 ? (
                        <div className="text-center py-16 text-gray-400 border border-dashed border-gray-200 rounded-xl">
                            <History className="h-10 w-10 mx-auto mb-2 opacity-30" />
                            <p className="font-medium">No executions yet.</p>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Script</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Executed by</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Executed at</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Result</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Rows</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide text-right pr-4">Export</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {executions.map((e) => (
                                        <TableRow key={e.id} className="hover:bg-gray-50">
                                            <TableCell className="font-medium text-[#417690]">{e.script_name}</TableCell>
                                            <TableCell className="text-sm text-gray-600">{e.executed_by?.username ?? "—"}</TableCell>
                                            <TableCell className="text-sm text-gray-500 whitespace-nowrap">{fmtDate(e.started_at)}</TableCell>
                                            <TableCell>
                                                {e.status === 'success' ? (
                                                    <span className="inline-flex items-center gap-1 text-green-700 text-sm font-medium">
                                                        <CheckCircle2 className="h-4 w-4" /> Success
                                                    </span>
                                                ) : e.status === 'error' || e.status === 'timeout' ? (
                                                    <span className="inline-flex items-center gap-1 text-red-600 text-sm font-medium">
                                                        <XCircle className="h-4 w-4" /> {e.status_display}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-gray-500 text-sm font-medium">
                                                        <Loader2 className="h-4 w-4 animate-spin" /> {e.status_display}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600">{e.row_count ?? "—"}</TableCell>
                                            <TableCell className="text-right pr-4">
                                                <div className="flex items-center justify-end gap-1">
                                                    <a
                                                        href={thuriyamService.exportExecutionCsv(e.id)}
                                                        download
                                                        className="inline-flex items-center gap-1 h-7 px-2.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                                                        title="Download CSV"
                                                    >
                                                        <Download className="h-3 w-3" />
                                                        CSV
                                                    </a>
                                                    <a
                                                        href={thuriyamService.exportExecutionPdf(e.id)}
                                                        download
                                                        className="inline-flex items-center gap-1 h-7 px-2.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                                                        title="Download PDF"
                                                    >
                                                        <Download className="h-3 w-3" />
                                                        PDF
                                                    </a>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5">
                                <span className="text-sm text-gray-600 font-medium">
                                    {executions.length} execution{executions.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* PERMISSIONS TAB */}
            {subTab === "permissions" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-lg font-semibold text-gray-800">Script Permissions</h2>
                            {permFilterScriptName && (
                                <Badge variant="outline" className="text-xs gap-1 font-normal">
                                    Filtered: <span className="font-medium">{permFilterScriptName}</span>
                                    <button
                                        onClick={() => { setPermFilterScriptId(null); setPermFilterScriptName(null); fetchPermissions(null); }}
                                        className="ml-1 text-gray-400 hover:text-gray-700 leading-none"
                                        title="Clear filter"
                                    >
                                        ×
                                    </button>
                                </Badge>
                            )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => fetchPermissions()} disabled={permissionsLoading} className="gap-1.5">
                            <RefreshCw className={`h-4 w-4 ${permissionsLoading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>

                    {permissionsLoading ? (
                        <div className="flex items-center justify-center py-16 text-gray-400">
                            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
                        </div>
                    ) : permissions.length === 0 ? (
                        <div className="text-center py-16 text-gray-400 border border-dashed border-gray-200 rounded-xl">
                            <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
                            <p className="font-medium">No permissions configured.</p>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Script</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">User</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Granted by</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide">Granted at</TableHead>
                                        <TableHead className="font-semibold text-gray-700 uppercase text-xs tracking-wide text-right pr-4">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {permissions.map((p) => (
                                        <TableRow key={p.id} className="hover:bg-gray-50">
                                            <TableCell className="font-medium text-[#417690]">{p.script_name}</TableCell>
                                            <TableCell className="text-sm text-gray-700">{p.user ? p.user.username : "—"}</TableCell>
                                            <TableCell className="text-sm text-gray-500">{p.granted_by ? p.granted_by.username : "—"}</TableCell>
                                            <TableCell className="text-sm text-gray-500 whitespace-nowrap">{fmtDate(p.granted_at)}</TableCell>
                                            <TableCell className="text-right pr-4">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleRevokePermission(p.id)}
                                                    className="h-7 px-3 text-xs text-red-500 border-red-200 hover:bg-red-50"
                                                >
                                                    Revoke
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5">
                                <span className="text-sm text-gray-600 font-medium">
                                    {permissions.length} permission{permissions.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ================================================================
                CREATE / EDIT SCRIPT DIALOG
            ================================================================ */}
            <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileCode2 className="h-5 w-5 text-intellend-green" />
                            {editingScript ? "Edit SQL Script" : "New SQL Script"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
                            <Input
                                value={scriptForm.name}
                                onChange={(e) => setScriptForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Post Disbursal Mandate Creation Data"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                            <Input
                                value={scriptForm.description}
                                onChange={(e) => setScriptForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder="What does this script do?"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Target Database *</label>
                            <Select
                                value={scriptForm.target_database}
                                onValueChange={(v) => setScriptForm((f) => ({ ...f, target_database: v }))}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="s1_apply_db">S1 - Apply Database</SelectItem>
                                    <SelectItem value="s2_applyv2_db">S2 - ApplyV2 Database</SelectItem>
                                    <SelectItem value="both">Both S1 and S2</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">SQL Content *</label>
                            <Textarea
                                value={scriptForm.sql_content}
                                onChange={(e) => setScriptForm((f) => ({ ...f, sql_content: e.target.value }))}
                                placeholder="SELECT * FROM ..."
                                className="font-mono text-sm min-h-[160px]"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Timeout (seconds)</label>
                                <Input
                                    type="number"
                                    value={scriptForm.timeout_seconds}
                                    onChange={(e) => setScriptForm((f) => ({ ...f, timeout_seconds: parseInt(e.target.value) || 30 }))}
                                    min={1}
                                    max={300}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-1 block">Max rows</label>
                                <Input
                                    type="number"
                                    value={scriptForm.max_rows}
                                    onChange={(e) => setScriptForm((f) => ({ ...f, max_rows: parseInt(e.target.value) || 1000 }))}
                                    min={1}
                                    max={10000}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={scriptForm.is_active}
                                onChange={(e) => setScriptForm((f) => ({ ...f, is_active: e.target.checked }))}
                                className="rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
                            />
                            <label htmlFor="is_active" className="text-sm text-gray-700">Active (can be executed)</label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScriptDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleSaveScript}
                            disabled={scriptSaving}
                            className="bg-intellend-green hover:bg-intellend-green/90 text-white"
                        >
                            {scriptSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editingScript ? "Save Changes" : "Create Script"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ================================================================
                EXECUTE SCRIPT DIALOG
            ================================================================ */}
            <Dialog
                open={execDialogOpen}
                onOpenChange={(open) => {
                    if (!open) { setExecDialogOpen(false); setExecResult(null); }
                    else setExecDialogOpen(true);
                }}
            >
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Play className="h-5 w-5 text-intellend-green" />
                            Execute: {execScript?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        {execScript && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Database className="h-3.5 w-3.5" />
                                <span>{dbLabel(execScript)}</span>
                            </div>
                        )}
                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                            <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                                {execScript?.sql_query}
                            </pre>
                        </div>
                        {execResult && (
                            <div className={`rounded-lg border p-3 text-sm ${execResult.status === 'success' ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                                {execResult.status === 'success' ? (
                                    <p className="flex items-center gap-1.5">
                                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                                        Success — {execResult.row_count ?? 0} row(s) affected.
                                    </p>
                                ) : (
                                    <p className="flex items-center gap-1.5">
                                        <XCircle className="h-4 w-4 shrink-0" />
                                        {execResult.error_message ?? "Execution failed."}
                                    </p>
                                )}
                                {execResult.output && (
                                    <pre className="mt-2 text-xs font-mono whitespace-pre-wrap break-all max-h-32 overflow-y-auto border-t border-current/20 pt-2">
                                        {execResult.output}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExecDialogOpen(false)}>Close</Button>
                        <Button
                            onClick={handleExecute}
                            disabled={execLoading}
                            className="bg-intellend-green hover:bg-intellend-green/90 text-white"
                        >
                            {execLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                            {execLoading ? "Running…" : "Run Script"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
};

export default SqlScriptExecutorPanel;
